import { cx } from '@apparel/react/cx';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Link from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { ErrorMessage, useFormikContext } from 'formik';
import React, { forwardRef, useCallback, useState } from 'react';
import { GetCompanyTitle } from '../../../../lib/utils';
import { AuthViewType } from '../../AuthButtonProps';
import { useNewAuthForm } from '../NewAuthFormContext';
import { useNewAuthDialogFormStyles } from './atoms';
import { FormHeader } from './FormHeader';

interface SignInProps extends React.HTMLAttributes<HTMLDivElement> {
    targetSignUpView?: AuthViewType;
    from?: string;
}

export const SignIn = forwardRef(({ className, targetSignUpView = 'signUp', from, ...rest }: SignInProps, ref: any) => {
    const classes = useNewAuthDialogFormStyles();
    const dialog$ = useNewAuthForm();
    const { handleChange, handleBlur, values, touched, errors } = useFormikContext<{
        password: string;
        identifier: string;
    }>();

    const handleView = useCallback((view: AuthViewType) => () => dialog$.setView(view), [dialog$.setView]);
    const [showPassword, setShowPassword] = useState(false);

    const handleToggleShowPassword = () => {
        setShowPassword(!showPassword);
    };

    return (
        <div className={cx(classes.root, className)} ref={ref} {...rest}>
            <FormHeader />

            <div className={classes.content}>
                <TextField
                    name={'identifier'}
                    label={'Email/Phone'}
                    value={values.identifier}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={classes.field}
                    error={errors.identifier && touched.identifier}
                    helperText={<ErrorMessage name={'identifier'} />}
                />

                <TextField
                    name={'password'}
                    label={'Password'}
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    type={showPassword ? 'text' : 'password'}
                    className={classes.field}
                    error={errors.password && touched.password}
                    helperText={<ErrorMessage name={'password'} />}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="Toggle password visibility"
                                    onClick={handleToggleShowPassword}
                                    onMouseDown={handleToggleShowPassword}
                                >
                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                        ),
                        autoComplete: 'off',
                    }}
                />

                <div className={classes.actions}>
                    <Button
                        type={'submit'}
                        variant={'contained'}
                        color={'primary'}
                        className={classes.button}
                        disableElevation
                    >
                        {dialog$.cta ?? 'Login'}
                    </Button>

                    <div className={classes.caption}>
                        <Link href="/platform/forgot">
                            <Typography variant={'caption'} color={'textPrimary'}>
                                Forgot Password?
                            </Typography>
                        </Link>
                    </div>
                </div>

                <Divider className={classes.divider} />
            </div>

            <div className={classes.footer}>
                <div className={classes.caption}>
                    <Typography variant={'caption'} color={'textPrimary'}>
                        New to {GetCompanyTitle()}?
                    </Typography>
                </div>
                <Button
                    type={'button'}
                    className={classes.button}
                    variant={'contained'}
                    onClick={handleView(targetSignUpView)}
                >
                    Sign Up
                </Button>
            </div>
        </div>
    );
});

export default SignIn;
